/**
 * AI Consensus Trading Bot Dashboard
 *
 * Multi-AI trading system with Claude, Grok, and ChatGPT
 * Redesigned UI inspired by professional trading interfaces
 */

import { useState, useEffect, useCallback } from 'react';
import { Icons } from '../components/Icons';
import {
  AIConsensusAPI,
  LogsAPI,
  type TradingDecision,
  type PromptTemplate,
  type ExchangeCredentials,
  type HeatmapData,
  type PerformanceMetrics,
  type APIStatusSummary,
  type APIMetrics,
  type TradingAccountInfo,
  type PendingOpportunity,
  type AutoAnalysisStatus,
  type TradingPairConfig,
  type BotInstance,
  type BotType,
  type BotServiceStatus,
  type SystemLog,
  type LogStats,
  type LogLevel,
  type LogCategory,
} from '../services/aiConsensusAPI';
import { PositionChart } from '../components/charts/PositionChart';
import { logger } from '../services/logger';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { AIConsensusAuthModal } from '../components/auth/AIConsensusAuthModal';
import { ExchangesTab } from '../components/exchanges/ExchangesTab';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';

type TabType = 'dashboard' | 'trading' | 'opportunities' | 'decisions' | 'prompts' | 'exchanges' | 'status' | 'logs';

// AI Provider colors
const AI_COLORS = {
  claude: '#8B5CF6',
  grok: '#F97316',
  chatgpt: '#22C55E',
};

// Action colors
const getActionColor = (action: string) => {
  switch (action) {
    case 'buy':
      return '#22C55E';
    case 'sell':
      return '#EF4444';
    case 'hold':
      return '#F59E0B';
    default:
      return '#6B7280';
  }
};

const getActionBg = (action: string) => {
  switch (action) {
    case 'buy':
      return 'rgba(34, 197, 94, 0.15)';
    case 'sell':
      return 'rgba(239, 68, 68, 0.15)';
    case 'hold':
      return 'rgba(245, 158, 11, 0.15)';
    default:
      return 'rgba(107, 114, 128, 0.15)';
  }
};

// Status colors
const getStatusColor = (status: string) => {
  switch (status) {
    case 'operational':
      return '#22C55E';
    case 'degraded':
      return '#F59E0B';
    case 'down':
      return '#EF4444';
    case 'unconfigured':
      return '#6B7280';
    default:
      return '#6B7280';
  }
};

// API Status Card Component
function APIStatusCard({ name, metrics }: { name: string; metrics: APIMetrics }) {
  const statusColor = getStatusColor(metrics.status);
  const successRate = metrics.totalCalls > 0
    ? ((metrics.successfulCalls / metrics.totalCalls) * 100).toFixed(1)
    : '0';

  return (
    <div style={{
      padding: '12px',
      marginBottom: '12px',
      background: 'var(--bg)',
      borderRadius: '8px',
      border: '1px solid var(--border)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: statusColor
          }} />
          <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{name}</span>
        </div>
        <span style={{
          fontSize: '12px',
          padding: '2px 8px',
          borderRadius: '4px',
          background: `${statusColor}20`,
          color: statusColor,
          fontWeight: 600,
          textTransform: 'uppercase'
        }}>
          {metrics.status}
        </span>
      </div>

      {metrics.isConfigured ? (
        <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Calls: {metrics.totalCalls}</span>
            <span>Success: {successRate}%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Avg Latency: {metrics.averageLatencyMs.toFixed(0)}ms</span>
            <span>Rate Limits: {metrics.rateLimitHits}</span>
          </div>
          {metrics.lastError && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#EF4444'
            }}>
              Last Error: {metrics.lastError.substring(0, 50)}...
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
          <span>⚠️ API key not configured</span>
        </div>
      )}
    </div>
  );
}

function AIConsensusBotContent() {
  // Auth state
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');

  // Backend state
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [decisions, setDecisions] = useState<TradingDecision[]>([]);
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [exchanges, setExchanges] = useState<ExchangeCredentials[]>([]);
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([]);
  const [performance, setPerformance] = useState<PerformanceMetrics | null>(null);
  const [apiStatus, setApiStatus] = useState<APIStatusSummary | null>(null);
  const [tradingAccount, setTradingAccount] = useState<TradingAccountInfo | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string | undefined>();

  // Analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisSymbol, setAnalysisSymbol] = useState('BTC/USDT');
  const [analysisTimeframe, setAnalysisTimeframe] = useState('1h');

  // Trading sub-tab state: 'overview' or position index (0, 1, 2, 3)
  const [tradingSubTab, setTradingSubTab] = useState<'overview' | number>('overview');

  // Proactive opportunities state
  const [opportunities, setOpportunities] = useState<PendingOpportunity[]>([]);
  const [autoAnalysisStatus, setAutoAnalysisStatus] = useState<AutoAnalysisStatus | null>(null);
  const [tradingPairs, setTradingPairs] = useState<TradingPairConfig[]>([]);

  // AI Supervised Bots state
  const [bots, setBots] = useState<BotInstance[]>([]);
  const [botServiceStatus, setBotServiceStatus] = useState<BotServiceStatus | null>(null);
  const [showCreateBotModal, setShowCreateBotModal] = useState(false);
  const [selectedBotType, setSelectedBotType] = useState<BotType | null>(null);

  // Logs state
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [logStats, setLogStats] = useState<LogStats | null>(null);
  const [logLevels, setLogLevels] = useState<LogLevel[]>([]);
  const [logCategories, setLogCategories] = useState<LogCategory[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState<{
    level?: string;
    category?: string;
    search?: string;
  }>({});
  const [logsPagination, setLogsPagination] = useState<{
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  }>({ total: 0, offset: 0, limit: 50, hasMore: false });

  // Check backend and load initial data
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);

      const available = await AIConsensusAPI.healthCheck();
      setBackendAvailable(available);

      if (available) {
        const [
          decisionsData,
          promptsData,
          exchangesData,
          heatmapResult,
          performanceData,
          apiStatusData,
          tradingAccountData,
          opportunitiesData,
          autoAnalysisData,
          tradingPairsData,
          botsData,
          botStatusData,
        ] = await Promise.all([
          AIConsensusAPI.getDecisions({ limit: 20 }),
          AIConsensusAPI.getPrompts(),
          AIConsensusAPI.getExchanges(),
          AIConsensusAPI.getHeatmapData(),
          AIConsensusAPI.getPerformance(),
          AIConsensusAPI.getAPIStatus(),
          AIConsensusAPI.getAccountInfo(),
          AIConsensusAPI.getOpportunities(),
          AIConsensusAPI.getAutoAnalysisStatus(),
          AIConsensusAPI.getTradingPairs(),
          AIConsensusAPI.getBots(),
          AIConsensusAPI.getBotServiceStatus(),
        ]);

        setDecisions(decisionsData);
        setPrompts(promptsData);
        setExchanges(exchangesData);
        setHeatmapData(heatmapResult);
        setPerformance(performanceData);
        setApiStatus(apiStatusData);
        setTradingAccount(tradingAccountData);
        setOpportunities(opportunitiesData);
        setAutoAnalysisStatus(autoAnalysisData);
        setTradingPairs(tradingPairsData);
        setBots(botsData);
        setBotServiceStatus(botStatusData);
      }

      setIsLoading(false);
    };

    initializeData();
  }, []);

  // Refresh data
  const refreshData = useCallback(async () => {
    if (!backendAvailable) return;

    const [decisionsData, heatmapResult, performanceData, apiStatusData, tradingAccountData, opportunitiesData, autoAnalysisData, botsData, botStatusData] = await Promise.all([
      AIConsensusAPI.getDecisions({ limit: 20 }),
      AIConsensusAPI.getHeatmapData(),
      AIConsensusAPI.getPerformance(),
      AIConsensusAPI.getAPIStatus(),
      AIConsensusAPI.getAccountInfo(),
      AIConsensusAPI.getOpportunities(),
      AIConsensusAPI.getAutoAnalysisStatus(),
      AIConsensusAPI.getBots(),
      AIConsensusAPI.getBotServiceStatus(),
    ]);

    setDecisions(decisionsData);
    setHeatmapData(heatmapResult);
    setPerformance(performanceData);
    setApiStatus(apiStatusData);
    setTradingAccount(tradingAccountData);
    setOpportunities(opportunitiesData);
    setAutoAnalysisStatus(autoAnalysisData);
    setBots(botsData);
    setBotServiceStatus(botStatusData);
  }, [backendAvailable]);

  // Fetch logs
  const fetchLogs = useCallback(async (append: boolean = false) => {
    if (!backendAvailable) return;

    setLogsLoading(true);
    try {
      const [logsResult, statsResult, levelsResult, categoriesResult] = await Promise.all([
        LogsAPI.getLogs({
          ...logFilter,
          limit: logsPagination.limit,
          offset: append ? logsPagination.offset + logsPagination.limit : 0,
        }),
        LogsAPI.getLogStats(),
        LogsAPI.getLogLevels(),
        LogsAPI.getLogCategories(),
      ]);

      if (logsResult) {
        if (append) {
          setLogs(prev => [...prev, ...logsResult.logs]);
        } else {
          setLogs(logsResult.logs);
        }
        setLogsPagination(logsResult.pagination);
      }
      if (statsResult) setLogStats(statsResult);
      if (levelsResult) setLogLevels(levelsResult);
      if (categoriesResult) setLogCategories(categoriesResult);
    } catch (error) {
      logger.error('AIConsensusBot', 'Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [backendAvailable, logFilter, logsPagination.limit, logsPagination.offset]);

  // Fetch logs when logs tab is active or filter changes
  useEffect(() => {
    if (activeTab === 'logs' && backendAvailable) {
      fetchLogs(false);
    }
  }, [activeTab, backendAvailable, logFilter]);

  // Trigger analysis
  const handleAnalyze = async () => {
    if (!backendAvailable || isAnalyzing) return;

    setIsAnalyzing(true);
    try {
      const result = await AIConsensusAPI.analyzeMarket({
        symbol: analysisSymbol,
        timeframe: analysisTimeframe,
        promptId: selectedPromptId,
      });

      if (result) {
        setDecisions((prev) => [result, ...prev]);
        setActiveTab('decisions');
      }
    } catch (error) {
      logger.error('AIConsensusBot', 'Analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Execute decision state
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [executeSuccess, setExecuteSuccess] = useState<string | null>(null);

  // Execution modal state
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState<TradingDecision | null>(null);
  const [positionPercent, setPositionPercent] = useState<number>(10);
  const [actionOverride, setActionOverride] = useState<'buy' | 'sell' | 'hold' | null>(null);

  // Calculate suggested position based on consensus confidence
  const getSuggestedPosition = (confidence: number) => {
    // Higher confidence = larger position (5% to 15% of portfolio)
    // confidence 50% → 5%, confidence 100% → 15%
    return Math.round(5 + (confidence - 50) * 0.2);
  };

  // Open execution modal
  const openExecuteModal = (decision: TradingDecision) => {
    setSelectedDecision(decision);
    setPositionPercent(getSuggestedPosition(decision.consensusConfidence));
    setActionOverride(null);
    setShowExecuteModal(true);
  };

  // Close modal
  const closeExecuteModal = () => {
    setShowExecuteModal(false);
    setSelectedDecision(null);
    setActionOverride(null);
  };

  // Confirm execution
  const confirmExecution = async () => {
    if (!selectedDecision) return;
    const currentPositionPercent = positionPercent;
    const currentOverride = actionOverride;
    closeExecuteModal();
    handleExecuteDecision(selectedDecision.id, currentOverride || undefined, currentPositionPercent);
  };

  // Execute decision
  const handleExecuteDecision = async (id: string, override?: 'buy' | 'sell' | 'hold', positionPct?: number) => {
    setExecutingId(id);
    setExecuteError(null);
    setExecuteSuccess(null);

    try {
      console.log('Executing decision:', id, override, 'positionPercent:', positionPct);
      const result = await AIConsensusAPI.executeDecision(id, { override, positionPercent: positionPct });
      console.log('Execute result:', result);

      if (result.success) {
        setExecuteSuccess(result.message || 'Decision executed successfully!');
        await refreshData();
        // Clear success message after 5 seconds
        setTimeout(() => setExecuteSuccess(null), 5000);
      } else {
        setExecuteError(result.error || 'Execution failed');
        // Clear error after 10 seconds
        setTimeout(() => setExecuteError(null), 10000);
      }
    } catch (error) {
      console.error('Execute error:', error);
      setExecuteError(error instanceof Error ? error.message : 'Unknown error');
      setTimeout(() => setExecuteError(null), 10000);
    } finally {
      setExecutingId(null);
    }
  };

  // Reset weights
  const handleResetWeights = async () => {
    const success = await AIConsensusAPI.resetWeights();
    if (success) {
      await refreshData();
    }
  };

  // Reset paper trading
  const handleResetPaperTrading = async () => {
    if (!confirm('Are you sure you want to reset your paper trading account? This will reset your balance to $10,000 and clear all positions.')) {
      return;
    }
    const success = await AIConsensusAPI.resetPaperTrading();
    if (success) {
      await refreshData();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '4px solid var(--accent)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--muted)' }}>Loading AI Consensus Bot...</p>
        </div>
      </div>
    );
  }

  // Backend unavailable
  if (!backendAvailable) {
    return (
      <div style={{ padding: '40px' }}>
        <div style={{
          maxWidth: '500px',
          margin: '80px auto',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'rgba(239, 68, 68, 0.15)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Icons.Zap size={40} style={{ color: '#EF4444' }} />
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            marginBottom: '16px'
          }}>
            Backend Not Available
          </h2>
          <p style={{
            color: 'var(--muted)',
            marginBottom: '24px',
            lineHeight: 1.6
          }}>
            The AI Consensus Bot backend is not running. Please start the backend service or check your configuration.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 32px',
              background: 'var(--accent)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Get latest decision for display
  const latestDecision = decisions[0];
  const weights = performance?.aiWeights || { claude: 0.333, grok: 0.333, chatgpt: 0.333 };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: 'var(--muted)'
      }}>
        <Icons.Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icons.Cpu size={32} />
            AI Consensus Bot
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
              Multi-AI Trading System • Claude + Grok + ChatGPT
            </p>
            {/* User Auth Status */}
            {isAuthenticated ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 12px',
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '16px',
                fontSize: '12px',
                color: '#22C55E'
              }}>
                <Icons.User size={14} />
                <span>{user?.email}</span>
                <button
                  onClick={logout}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--muted)',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    marginLeft: '4px'
                  }}
                  title="Deconnexion"
                >
                  <Icons.LogOut size={12} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 12px',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                <Icons.LogIn size={14} />
                Connexion
              </button>
            )}
          </div>
        </div>

        {/* Quick Analysis Controls */}
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <select
            aria-label="Analysis symbol"
            value={analysisSymbol}
            onChange={(e) => setAnalysisSymbol(e.target.value)}
            style={{
              padding: '10px 14px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="BTC/USDT">BTC/USDT</option>
            <option value="ETH/USDT">ETH/USDT</option>
            <option value="SOL/USDT">SOL/USDT</option>
            <option value="XRP/USDT">XRP/USDT</option>
            <option value="CRO/USDT">CRO/USDT</option>
          </select>
          <select
            aria-label="Analysis timeframe"
            value={analysisTimeframe}
            onChange={(e) => setAnalysisTimeframe(e.target.value)}
            style={{
              padding: '10px 14px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="5m">5 min</option>
            <option value="15m">15 min</option>
            <option value="1h">1 hour</option>
            <option value="4h">4 hours</option>
            <option value="1d">1 day</option>
          </select>
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            style={{
              padding: '10px 24px',
              background: isAnalyzing ? 'var(--muted)' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: isAnalyzing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isAnalyzing ? (
              <>
                <Icons.RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Analyzing...
              </>
            ) : (
              <>
                <Icons.Zap size={16} />
                Analyze Now
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '4px',
        background: 'var(--border)',
        padding: '4px',
        borderRadius: '12px',
        marginBottom: '24px',
        width: 'fit-content'
      }}>
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Icons.Activity },
          { id: 'trading', label: 'Trading', icon: Icons.DollarSign },
          { id: 'opportunities', label: 'Opportunities', icon: Icons.Zap, badge: opportunities.length },
          { id: 'decisions', label: 'Decisions', icon: Icons.List },
          { id: 'prompts', label: 'Prompts', icon: Icons.Edit },
          { id: 'exchanges', label: 'Exchanges', icon: Icons.Wallet },
          { id: 'status', label: 'API Status', icon: Icons.Server },
          { id: 'logs', label: 'Logs', icon: Icons.FileText },
        ].map(({ id, label, icon: Icon, badge }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as TabType)}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              background: activeTab === id ? 'var(--accent)' : 'transparent',
              color: activeTab === id ? 'white' : 'var(--text)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            <Icon size={18} />
            {label}
            {badge !== undefined && badge > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#EF4444',
                color: 'white',
                fontSize: '11px',
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center'
              }}>
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '20px' }}>
          {/* Left Column - AI Chat Room */}
          <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{
                fontSize: '16px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icons.MessageSquare size={18} />
                AI Chat Room
              </h2>
              <Icons.Filter size={16} style={{ color: 'var(--muted)', cursor: 'pointer' }} />
            </div>

            <div style={{ padding: '16px', maxHeight: '500px', overflowY: 'auto' }}>
              {latestDecision ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* ChatGPT */}
                  {latestDecision.chatgptResponse && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: AI_COLORS.chatgpt,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{ fontSize: '14px' }}>🤖</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>GPT-4</span>
                          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                            {new Date(latestDecision.chatgptResponse.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p style={{
                          fontSize: '13px',
                          color: 'var(--text)',
                          lineHeight: 1.5,
                          margin: 0
                        }}>
                          {latestDecision.chatgptResponse.reasoning}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Claude */}
                  {latestDecision.claudeResponse && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: AI_COLORS.claude,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{ fontSize: '14px' }}>🧠</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>Claude</span>
                          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                            {new Date(latestDecision.claudeResponse.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p style={{
                          fontSize: '13px',
                          color: 'var(--text)',
                          lineHeight: 1.5,
                          margin: 0
                        }}>
                          {latestDecision.claudeResponse.reasoning}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Grok */}
                  {latestDecision.grokResponse && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: AI_COLORS.grok,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{ fontSize: '14px' }}>⚡</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>Grok</span>
                          <span style={{
                            padding: '2px 8px',
                            background: getActionBg(latestDecision.grokResponse.action),
                            color: getActionColor(latestDecision.grokResponse.action),
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 700,
                            textTransform: 'uppercase'
                          }}>
                            {latestDecision.grokResponse.action}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                            {latestDecision.grokResponse.confidence}% conf
                          </span>
                        </div>
                        <p style={{
                          fontSize: '13px',
                          color: 'var(--text)',
                          lineHeight: 1.5,
                          margin: 0
                        }}>
                          {latestDecision.grokResponse.reasoning}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
                  <Icons.MessageSquare size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
                  <p>No AI discussions yet.<br />Click "Analyze Now" to start.</p>
                </div>
              )}
            </div>
          </div>

          {/* Center Column - Performance */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* KPI Cards - Using real trading account data */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Portfolio</div>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>
                  ${tradingAccount?.account?.totalValueUsdt?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '10,000.00'}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: (tradingAccount?.pnlStats?.totalPnlPercent || 0) >= 0 ? '#22C55E' : '#EF4444',
                  marginTop: '4px'
                }}>
                  {(tradingAccount?.pnlStats?.totalPnlPercent || 0) >= 0 ? '+' : ''}
                  {(tradingAccount?.pnlStats?.totalPnlPercent || 0).toFixed(2)}%
                </div>
              </div>

              <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Win Rate</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#22C55E' }}>
                  {tradingAccount?.pnlStats?.winRate?.toFixed(1) || performance?.winRate?.toFixed(1) || 0}%
                </div>
              </div>

              <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Decisions</div>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>
                  {decisions.length || performance?.totalDecisions || 0}
                </div>
              </div>

              <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Open Positions</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#8B5CF6' }}>
                  {tradingAccount?.positions?.length || 0}
                </div>
              </div>
            </div>

            {/* Performance Chart Placeholder */}
            <div className="card" style={{ padding: '20px', flex: 1 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icons.TrendingUp size={18} />
                  Performance
                </h3>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['1H', '6H', '24H', '7D', '30D'].map((period) => (
                    <button
                      key={period}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        background: period === '24H' ? 'var(--accent)' : 'var(--border)',
                        color: period === '24H' ? 'white' : 'var(--text)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{
                height: '200px',
                background: 'var(--border)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--muted)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Icons.TrendingUp size={40} style={{ opacity: 0.3, marginBottom: '8px' }} />
                  <p>Performance chart coming soon</p>
                </div>
              </div>
            </div>

            {/* Trade History */}
            <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icons.Clock size={18} />
                  Trade History
                </h3>
                <span style={{
                  padding: '4px 10px',
                  background: 'var(--border)',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {decisions.length} trades
                </span>
              </div>

              {/* V2.I.7.f — table migrée vers ResponsiveTable. */}
              <ResponsiveTable<TradingDecision>
                data={decisions.slice(0, 5)}
                keyExtractor={(d) => d.id}
                emptyState={
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                    No trade history yet
                  </div>
                }
                columns={[
                  {
                    key: 'time',
                    header: 'Time',
                    isSubtitle: true,
                    render: (d) => (
                      <span style={{ color: 'var(--muted)' }}>
                        {new Date(d.createdAt).toLocaleTimeString()}
                      </span>
                    ),
                  },
                  {
                    key: 'symbol',
                    header: 'Symbol',
                    isTitle: true,
                    render: (d) => <span style={{ fontWeight: 600 }}>{d.symbol}</span>,
                  },
                  {
                    key: 'decision',
                    header: 'Decision',
                    render: (d) => (
                      <span style={{
                        padding: '4px 10px',
                        background: getActionBg(d.consensusAction),
                        color: getActionColor(d.consensusAction),
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}>
                        {d.consensusAction}
                      </span>
                    ),
                  },
                  {
                    key: 'votes',
                    header: 'Votes',
                    render: (d) => (
                      <span style={{ color: 'var(--muted)', fontSize: '12px' }}>
                        {[d.claudeResponse, d.grokResponse, d.chatgptResponse]
                          .filter(r => r?.action === d.consensusAction).length}/3
                      </span>
                    ),
                  },
                  {
                    key: 'impact',
                    header: 'Impact',
                    align: 'right',
                    render: (d) => (
                      <span style={{
                        fontWeight: 600,
                        color: (d.profitLossPercent || 0) >= 0 ? '#22C55E' : '#EF4444',
                      }}>
                        {d.executed && d.profitLossPercent !== null
                          ? `${d.profitLossPercent >= 0 ? '+' : ''}${d.profitLossPercent.toFixed(2)}%`
                          : '—'}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          </div>

          {/* Right Column - Consensus Vote */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Consensus Panel */}
            <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icons.Check size={18} />
                <span style={{ fontWeight: 600 }}>Consensus Vote</span>
              </div>

              <div style={{ padding: '20px' }}>
                {/* Current Consensus */}
                {latestDecision && (
                  <div style={{
                    padding: '16px',
                    background: getActionBg(latestDecision.consensusAction),
                    borderRadius: '12px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Current Signal</div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: getActionColor(latestDecision.consensusAction),
                      textTransform: 'uppercase'
                    }}>
                      {latestDecision.consensusAction}
                    </div>
                  </div>
                )}

                {/* Vote Breakdown */}
                <div style={{ marginBottom: '20px' }}>
                  {['buy', 'hold', 'sell'].map((action) => {
                    const count = latestDecision
                      ? [latestDecision.claudeResponse, latestDecision.grokResponse, latestDecision.chatgptResponse]
                          .filter(r => r?.action === action).length
                      : 0;
                    const total = latestDecision ? 3 : 0;
                    const percentage = total > 0 ? (count / total) * 100 : 0;

                    return (
                      <div key={action} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: getActionColor(action),
                            textTransform: 'uppercase'
                          }}>
                            {action}
                          </span>
                          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                            {count}/{total}
                          </span>
                        </div>
                        <div style={{
                          height: '8px',
                          background: 'var(--border)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${percentage}%`,
                            background: getActionColor(action),
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Average Confidence */}
                <div style={{
                  padding: '16px',
                  background: 'var(--border)',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Average Confidence</div>
                  <div style={{ fontSize: '32px', fontWeight: 700 }}>
                    {latestDecision
                      ? Math.round(
                          ([latestDecision.claudeResponse?.confidence, latestDecision.grokResponse?.confidence, latestDecision.chatgptResponse?.confidence]
                            .filter(Boolean) as number[])
                            .reduce((a, b) => a + b, 0) /
                          [latestDecision.claudeResponse, latestDecision.grokResponse, latestDecision.chatgptResponse].filter(Boolean).length || 1
                        )
                      : 0}%
                  </div>
                </div>
              </div>
            </div>

            {/* Individual AI Votes */}
            <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--muted)' }}>
                Individual Votes
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* GPT */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'var(--border)',
                  borderRadius: '10px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: AI_COLORS.chatgpt,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: '14px' }}>🤖</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>GPT</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      {latestDecision?.chatgptResponse?.action?.toUpperCase() || 'N/A'}
                    </div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700 }}>
                    {latestDecision?.chatgptResponse?.confidence || 0}%
                  </div>
                </div>

                {/* Claude */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'var(--border)',
                  borderRadius: '10px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: AI_COLORS.claude,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: '14px' }}>🧠</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>CLD</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      {latestDecision?.claudeResponse?.action?.toUpperCase() || 'N/A'}
                    </div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700 }}>
                    {latestDecision?.claudeResponse?.confidence || 0}%
                  </div>
                </div>

                {/* Grok */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'var(--border)',
                  borderRadius: '10px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: AI_COLORS.grok,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: '14px' }}>⚡</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>GRK</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      {latestDecision?.grokResponse?.action?.toUpperCase() || 'N/A'}
                    </div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700 }}>
                    {latestDecision?.grokResponse?.confidence || 0}%
                  </div>
                </div>
              </div>
            </div>

            {/* AI Weights */}
            <div className="card" style={{ padding: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--muted)' }}>
                  AI Weights
                </h3>
                <button
                  onClick={handleResetWeights}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--muted)',
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(weights).map(([ai, weight]) => (
                  <div key={ai} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: AI_COLORS[ai as keyof typeof AI_COLORS]
                    }} />
                    <span style={{ fontSize: '12px', textTransform: 'capitalize', flex: 1 }}>{ai}</span>
                    <div style={{
                      width: '100px',
                      height: '6px',
                      background: 'var(--border)',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${weight * 100}%`,
                        background: AI_COLORS[ai as keyof typeof AI_COLORS],
                        borderRadius: '3px'
                      }} />
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 600, width: '40px', textAlign: 'right' }}>
                      {(weight * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trading Tab */}
      {activeTab === 'trading' && (
        <div>
          {/* Trading Mode Banner */}
          <div className="card" style={{
            padding: '20px',
            marginBottom: '20px',
            background: tradingAccount?.isPaperTrading
              ? 'rgba(245, 158, 11, 0.1)'
              : 'rgba(34, 197, 94, 0.1)',
            border: `1px solid ${tradingAccount?.isPaperTrading ? '#F59E0B' : '#22C55E'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: tradingAccount?.isPaperTrading ? '#F59E0B' : '#22C55E',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {tradingAccount?.isPaperTrading ? (
                    <Icons.FileText size={24} style={{ color: 'white' }} />
                  ) : (
                    <Icons.DollarSign size={24} style={{ color: 'white' }} />
                  )}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px' }}>
                    {tradingAccount?.isPaperTrading ? 'Paper Trading Mode' : 'Live Trading Mode'}
                  </h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '14px' }}>
                    {tradingAccount?.isPaperTrading
                      ? 'Simulated trading with virtual funds - no real money at risk'
                      : 'Connected to Binance Testnet - trades are executed on the exchange'}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={refreshData}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--border)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icons.RefreshCw size={16} />
                  Refresh
                </button>
                {tradingAccount?.isPaperTrading && (
                  <button
                    onClick={handleResetPaperTrading}
                    style={{
                      padding: '10px 20px',
                      background: '#EF4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Icons.RefreshCw size={16} />
                    Reset Account
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Account Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Total Balance</div>
              <div style={{ fontSize: '28px', fontWeight: 700 }}>
                ${tradingAccount?.account?.totalValueUsdt?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>USDT</div>
            </div>

            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Total P&L</div>
              <div style={{
                fontSize: '28px',
                fontWeight: 700,
                color: (tradingAccount?.pnlStats?.totalPnl || 0) >= 0 ? '#22C55E' : '#EF4444'
              }}>
                {(tradingAccount?.pnlStats?.totalPnl || 0) >= 0 ? '+' : ''}
                ${Math.abs(tradingAccount?.pnlStats?.totalPnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{
                fontSize: '12px',
                color: (tradingAccount?.pnlStats?.totalPnlPercent || 0) >= 0 ? '#22C55E' : '#EF4444',
                marginTop: '4px'
              }}>
                {(tradingAccount?.pnlStats?.totalPnlPercent || 0) >= 0 ? '+' : ''}
                {(tradingAccount?.pnlStats?.totalPnlPercent || 0).toFixed(2)}%
              </div>
            </div>

            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Win Rate</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#22C55E' }}>
                {(tradingAccount?.pnlStats?.winRate || 0).toFixed(1)}%
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                Success Rate
              </div>
            </div>

            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Open Positions</div>
              <div style={{ fontSize: '28px', fontWeight: 700 }}>
                {tradingAccount?.positions?.length || 0}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                Active Trades
              </div>
            </div>
          </div>

          {/* Sub-tabs Navigation */}
          <div className="card" style={{ padding: '0', marginBottom: '20px', overflow: 'hidden' }}>
            <div style={{
              display: 'flex',
              borderBottom: '1px solid var(--border)',
              overflowX: 'auto',
            }}>
              {/* Overview Tab */}
              <button
                onClick={() => setTradingSubTab('overview')}
                style={{
                  padding: '14px 24px',
                  background: tradingSubTab === 'overview' ? 'var(--background)' : 'transparent',
                  border: 'none',
                  borderBottom: tradingSubTab === 'overview' ? '2px solid var(--accent)' : '2px solid transparent',
                  color: tradingSubTab === 'overview' ? 'var(--accent)' : 'var(--muted)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                <Icons.Grid size={16} />
                Overview
              </button>

              {/* Position Tabs */}
              {tradingAccount?.positions?.slice(0, 4).map((position, index) => (
                <button
                  key={index}
                  onClick={() => setTradingSubTab(index)}
                  style={{
                    padding: '14px 24px',
                    background: tradingSubTab === index ? 'var(--background)' : 'transparent',
                    border: 'none',
                    borderBottom: tradingSubTab === index ? '2px solid var(--accent)' : '2px solid transparent',
                    color: tradingSubTab === index ? 'var(--accent)' : 'var(--muted)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: position.symbol.includes('BTC') ? '#F7931A' :
                               position.symbol.includes('ETH') ? '#627EEA' :
                               position.symbol.includes('SOL') ? '#9945FF' :
                               position.symbol.includes('XRP') ? '#23292F' : '#8B5CF6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '8px'
                  }}>
                    {position.symbol.replace('USDT', '').substring(0, 2)}
                  </div>
                  {position.symbol.replace('USDT', '')}
                  <span style={{
                    fontSize: '11px',
                    color: position.unrealizedPnl >= 0 ? '#22C55E' : '#EF4444',
                    fontWeight: 700,
                  }}>
                    {position.unrealizedPnl >= 0 ? '+' : ''}{position.unrealizedPnlPercent.toFixed(1)}%
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Overview Sub-Tab Content */}
          {tradingSubTab === 'overview' && (
            <>
              {/* Position Grid Preview */}
              {tradingAccount?.positions && tradingAccount.positions.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: tradingAccount.positions.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                  gap: '16px',
                  marginBottom: '20px',
                }}>
                  {tradingAccount.positions.slice(0, 4).map((position, index) => (
                    <div
                      key={index}
                      className="card"
                      style={{
                        padding: '0',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                      }}
                      onClick={() => setTradingSubTab(index)}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      {/* Position Header */}
                      <div style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: position.symbol.includes('BTC') ? '#F7931A' :
                                       position.symbol.includes('ETH') ? '#627EEA' :
                                       position.symbol.includes('SOL') ? '#9945FF' :
                                       position.symbol.includes('XRP') ? '#23292F' : '#8B5CF6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '10px'
                          }}>
                            {position.symbol.replace('USDT', '').substring(0, 3)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '14px' }}>{position.symbol}</div>
                            <div style={{
                              fontSize: '11px',
                              color: position.side === 'LONG' ? '#22C55E' : '#EF4444',
                              fontWeight: 600
                            }}>
                              {position.side} • {position.quantity.toFixed(position.symbol.includes('BTC') ? 6 : 4)}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            color: position.unrealizedPnl >= 0 ? '#22C55E' : '#EF4444'
                          }}>
                            {position.unrealizedPnl >= 0 ? '+' : ''}${position.unrealizedPnl.toFixed(2)}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: position.unrealizedPnlPercent >= 0 ? '#22C55E' : '#EF4444'
                          }}>
                            {position.unrealizedPnlPercent >= 0 ? '+' : ''}{position.unrealizedPnlPercent.toFixed(2)}%
                          </div>
                        </div>
                      </div>

                      {/* Mini Chart */}
                      <PositionChart
                        symbol={position.symbol}
                        positions={[position]}
                        height={180}
                      />

                      {/* Click to expand hint */}
                      <div style={{
                        padding: '8px 16px',
                        borderTop: '1px solid var(--border)',
                        textAlign: 'center',
                        fontSize: '11px',
                        color: 'var(--muted)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}>
                        <Icons.Maximize size={12} />
                        Click to view details
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card" style={{
                  padding: '60px 20px',
                  textAlign: 'center',
                  marginBottom: '20px',
                }}>
                  <Icons.TrendingUp size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                  <h4 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600 }}>No Active Positions</h4>
                  <p style={{ margin: 0, color: 'var(--muted)', fontSize: '14px' }}>
                    Execute a trade from the Decisions tab to see position charts here
                  </p>
                </div>
              )}

              {/* Balances & Quick Actions Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {/* Balances */}
                <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Icons.Wallet size={18} />
                    <span style={{ fontWeight: 600 }}>Asset Balances</span>
                  </div>
                  <div style={{ padding: '16px' }}>
                    {tradingAccount?.account?.balances && tradingAccount.account.balances.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {tradingAccount.account.balances.map((balance) => (
                          <div key={balance.asset} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            background: 'var(--border)',
                            borderRadius: '8px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: balance.asset === 'USDT' ? '#22C55E' :
                                           balance.asset === 'BTC' ? '#F7931A' :
                                           balance.asset === 'ETH' ? '#627EEA' : '#8B5CF6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '12px'
                              }}>
                                {balance.asset.substring(0, 3)}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600 }}>{balance.asset}</div>
                                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                                  Free: {balance.free.toFixed(balance.asset === 'USDT' ? 2 : 8)}
                                </div>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700 }}>
                                {balance.total.toFixed(balance.asset === 'USDT' ? 2 : 8)}
                              </div>
                              {balance.locked > 0 && (
                                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                                  Locked: {balance.locked.toFixed(8)}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                        <Icons.Wallet size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
                        <p>No balances found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Icons.Zap size={18} />
                    <span style={{ fontWeight: 600 }}>Quick Actions</span>
                  </div>
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <button
                      onClick={() => setActiveTab('decisions')}
                      style={{
                        padding: '14px 20px',
                        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <Icons.List size={18} />
                      View Decisions
                    </button>
                    <button
                      onClick={handleAnalyze}
                      disabled={isAnalyzing}
                      style={{
                        padding: '14px 20px',
                        background: isAnalyzing ? 'var(--muted)' : '#22C55E',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      {isAnalyzing ? (
                        <>
                          <Icons.RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Icons.Zap size={18} />
                          New Analysis
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('status')}
                      style={{
                        padding: '14px 20px',
                        background: 'var(--border)',
                        color: 'var(--text)',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <Icons.Server size={18} />
                      API Status
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Individual Position Detail View */}
          {typeof tradingSubTab === 'number' && tradingAccount?.positions?.[tradingSubTab] && (
            <>
              {(() => {
                const position = tradingAccount.positions[tradingSubTab];
                return (
                  <>
                    {/* Large Chart */}
                    <div className="card" style={{ padding: '0', overflow: 'hidden', marginBottom: '20px' }}>
                      <div style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid var(--border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: position.symbol.includes('BTC') ? '#F7931A' :
                                       position.symbol.includes('ETH') ? '#627EEA' :
                                       position.symbol.includes('SOL') ? '#9945FF' :
                                       position.symbol.includes('XRP') ? '#23292F' : '#8B5CF6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '12px'
                          }}>
                            {position.symbol.replace('USDT', '').substring(0, 3)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '18px' }}>{position.symbol}</div>
                            <div style={{
                              fontSize: '13px',
                              color: position.side === 'LONG' ? '#22C55E' : '#EF4444',
                              fontWeight: 600
                            }}>
                              {position.side} Position
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: position.unrealizedPnl >= 0 ? '#22C55E' : '#EF4444'
                          }}>
                            {position.unrealizedPnl >= 0 ? '+' : ''}${position.unrealizedPnl.toFixed(2)}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: position.unrealizedPnlPercent >= 0 ? '#22C55E' : '#EF4444'
                          }}>
                            {position.unrealizedPnlPercent >= 0 ? '+' : ''}{position.unrealizedPnlPercent.toFixed(2)}%
                          </div>
                        </div>
                      </div>

                      <PositionChart
                        symbol={position.symbol}
                        positions={[position]}
                        height={400}
                      />

                      {/* Chart Legend */}
                      <div style={{
                        padding: '12px 20px',
                        borderTop: '1px solid var(--border)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        fontSize: '11px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#f59e0b', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>SMA 20</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#3b82f6', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>SMA 50</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#22c55e', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>Entry</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#8b5cf6', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>Current</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', borderLeft: '1px solid var(--border)', paddingLeft: '16px' }}>
                          <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Fib:</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#6366f1', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>0%</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#a855f7', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>38.2%</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#d946ef', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>50%</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#ec4899', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>61.8%</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '2px', background: '#ef4444', borderRadius: '1px' }} />
                          <span style={{ color: 'var(--muted)' }}>100%</span>
                        </div>
                      </div>
                    </div>

                    {/* Position Details Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
                      <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Entry Price</div>
                        <div style={{ fontSize: '24px', fontWeight: 700 }}>
                          ${position.entryPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Current Price</div>
                        <div style={{ fontSize: '24px', fontWeight: 700 }}>
                          ${position.currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Quantity</div>
                        <div style={{ fontSize: '24px', fontWeight: 700 }}>
                          {position.quantity.toFixed(position.symbol.includes('BTC') ? 6 : 4)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                          {position.symbol.replace('USDT', '')}
                        </div>
                      </div>
                      <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Position Value</div>
                        <div style={{ fontSize: '24px', fontWeight: 700 }}>
                          ${(position.quantity * position.currentPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>USDT</div>
                      </div>
                    </div>

                    {/* Position Actions */}
                    <div className="card" style={{ padding: '20px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icons.Zap size={18} />
                        Position Actions
                      </h3>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setTradingSubTab('overview')}
                          style={{
                            padding: '12px 24px',
                            background: 'var(--border)',
                            color: 'var(--text)',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <Icons.ArrowLeft size={16} />
                          Back to Overview
                        </button>
                        <button
                          onClick={refreshData}
                          style={{
                            padding: '12px 24px',
                            background: 'var(--accent)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <Icons.RefreshCw size={16} />
                          Refresh Position
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* Opportunities Tab */}
      {activeTab === 'opportunities' && (
        <div>
          {/* Service Status Banner */}
          <div className="card" style={{
            padding: '20px',
            marginBottom: '20px',
            background: autoAnalysisStatus?.isRunning
              ? 'rgba(34, 197, 94, 0.1)'
              : 'rgba(107, 114, 128, 0.1)',
            border: `1px solid ${autoAnalysisStatus?.isRunning ? '#22C55E' : '#6B7280'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: autoAnalysisStatus?.isRunning ? '#22C55E' : '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icons.Zap size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px' }}>
                    Proactive Analysis {autoAnalysisStatus?.isRunning ? 'Active' : 'Inactive'}
                  </h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '14px' }}>
                    {autoAnalysisStatus?.isRunning
                      ? `Analyzing ${autoAnalysisStatus.configuredPairs} pairs every ${autoAnalysisStatus.config?.intervalMinutes || 15} minutes`
                      : 'Start the service to receive proactive trading opportunities'}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={async () => {
                    if (autoAnalysisStatus?.isRunning) {
                      await AIConsensusAPI.stopAutoAnalysis();
                    } else {
                      await AIConsensusAPI.startAutoAnalysis();
                    }
                    await refreshData();
                  }}
                  style={{
                    padding: '10px 20px',
                    background: autoAnalysisStatus?.isRunning ? '#EF4444' : '#22C55E',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {autoAnalysisStatus?.isRunning ? (
                    <>
                      <Icons.Square size={16} />
                      Stop
                    </>
                  ) : (
                    <>
                      <Icons.Play size={16} />
                      Start
                    </>
                  )}
                </button>
                <button
                  onClick={async () => {
                    await AIConsensusAPI.triggerAnalysis();
                    await refreshData();
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--border)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icons.RefreshCw size={16} />
                  Analyze Now
                </button>
              </div>
            </div>

            {/* Last/Next Analysis */}
            {autoAnalysisStatus?.lastAnalysisTime && (
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                gap: '24px',
                fontSize: '13px',
                color: 'var(--muted)'
              }}>
                <span>
                  Last analysis: {new Date(autoAnalysisStatus.lastAnalysisTime).toLocaleString()}
                </span>
                {autoAnalysisStatus.nextAnalysisTime && (
                  <span>
                    Next analysis: {new Date(autoAnalysisStatus.nextAnalysisTime).toLocaleString()}
                  </span>
                )}
              </div>
            )}

            {/* AI Providers Status */}
            {autoAnalysisStatus?.aiAvailability && (
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: '13px', marginBottom: '8px', fontWeight: 600 }}>
                  AI Providers Status
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    background: autoAnalysisStatus.aiAvailability.claude ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: autoAnalysisStatus.aiAvailability.claude ? '#22C55E' : '#EF4444',
                    border: `1px solid ${autoAnalysisStatus.aiAvailability.claude ? '#22C55E' : '#EF4444'}`
                  }}>
                    Claude: {autoAnalysisStatus.aiAvailability.claude ? 'Configured' : 'Not configured'}
                  </span>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    background: autoAnalysisStatus.aiAvailability.grok ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: autoAnalysisStatus.aiAvailability.grok ? '#22C55E' : '#EF4444',
                    border: `1px solid ${autoAnalysisStatus.aiAvailability.grok ? '#22C55E' : '#EF4444'}`
                  }}>
                    Grok: {autoAnalysisStatus.aiAvailability.grok ? 'Configured' : 'Not configured'}
                  </span>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    background: autoAnalysisStatus.aiAvailability.chatgpt ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: autoAnalysisStatus.aiAvailability.chatgpt ? '#22C55E' : '#EF4444',
                    border: `1px solid ${autoAnalysisStatus.aiAvailability.chatgpt ? '#22C55E' : '#EF4444'}`
                  }}>
                    ChatGPT: {autoAnalysisStatus.aiAvailability.chatgpt ? 'Configured' : 'Not configured'}
                  </span>
                </div>
                {!autoAnalysisStatus.aiAvailability.anyAvailable && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '8px',
                    color: '#EF4444',
                    fontSize: '13px'
                  }}>
                    <strong>Warning:</strong> No AI providers configured. Please add at least one API key (ANTHROPIC_API_KEY, OPENAI_API_KEY, or XAI_API_KEY) to enable analysis.
                  </div>
                )}
              </div>
            )}

            {/* Last Analysis Error */}
            {autoAnalysisStatus?.lastAnalysisError && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                border: '1px solid #EF4444',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#EF4444' }}>
                  <Icons.AlertTriangle size={16} />
                  <span style={{ fontWeight: 600, fontSize: '13px' }}>Last Analysis Error</span>
                </div>
                <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#EF4444' }}>
                  {autoAnalysisStatus.lastAnalysisError}
                </p>
              </div>
            )}
          </div>

          {/* Pending Opportunities */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.TrendingUp size={18} />
                Pending Opportunities ({opportunities.length})
              </h3>

              {opportunities.length === 0 ? (
                <div className="card" style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                  <Icons.Inbox size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
                  <p>No pending opportunities</p>
                  <p style={{ fontSize: '13px' }}>
                    {autoAnalysisStatus?.isRunning
                      ? 'The service is analyzing markets. New opportunities will appear here.'
                      : 'Start the proactive analysis service to receive opportunities.'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {opportunities.map((opp) => (
                    <div
                      key={opp.id}
                      className="card"
                      style={{
                        padding: '16px',
                        border: `2px solid ${opp.action === 'buy' ? '#22C55E' : '#EF4444'}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 700, fontSize: '18px' }}>{opp.symbol}</span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              background: opp.tradingMode === 'futures' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                              color: opp.tradingMode === 'futures' ? '#8B5CF6' : '#22C55E'
                            }}>
                              {opp.tradingMode}
                            </span>
                            {opp.suggestedLeverage && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: 'rgba(245, 158, 11, 0.2)',
                                color: '#F59E0B'
                              }}>
                                {opp.suggestedLeverage}x
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                            Expires: {new Date(opp.expiresAt).toLocaleTimeString()}
                          </div>
                        </div>
                        <div style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          background: getActionBg(opp.action),
                          color: getActionColor(opp.action),
                          fontWeight: 700,
                          fontSize: '16px',
                          textTransform: 'uppercase'
                        }}>
                          {opp.action}
                        </div>
                      </div>

                      {/* Confidence and AI Votes */}
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Confidence</div>
                          <div style={{ fontWeight: 700, fontSize: '20px' }}>{opp.confidence.toFixed(1)}%</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Consensus</div>
                          <div style={{ fontWeight: 700, fontSize: '20px' }}>{opp.consensusScore.toFixed(1)}%</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Est. Value</div>
                          <div style={{ fontWeight: 700, fontSize: '20px' }}>${opp.estimatedValue.toFixed(2)}</div>
                        </div>
                      </div>

                      {/* AI Votes */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                        {opp.votes.claude && (
                          <div style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '8px',
                            background: 'rgba(139, 92, 246, 0.1)',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '11px', color: '#8B5CF6', marginBottom: '2px' }}>Claude</div>
                            <div style={{ fontWeight: 600, fontSize: '12px', color: getActionColor(opp.votes.claude.action) }}>
                              {opp.votes.claude.action.toUpperCase()} ({opp.votes.claude.confidence}%)
                            </div>
                          </div>
                        )}
                        {opp.votes.grok && (
                          <div style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '8px',
                            background: 'rgba(249, 115, 22, 0.1)',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '11px', color: '#F97316', marginBottom: '2px' }}>Grok</div>
                            <div style={{ fontWeight: 600, fontSize: '12px', color: getActionColor(opp.votes.grok.action) }}>
                              {opp.votes.grok.action.toUpperCase()} ({opp.votes.grok.confidence}%)
                            </div>
                          </div>
                        )}
                        {opp.votes.chatgpt && (
                          <div style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '8px',
                            background: 'rgba(34, 197, 94, 0.1)',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '11px', color: '#22C55E', marginBottom: '2px' }}>GPT</div>
                            <div style={{ fontWeight: 600, fontSize: '12px', color: getActionColor(opp.votes.chatgpt.action) }}>
                              {opp.votes.chatgpt.action.toUpperCase()} ({opp.votes.chatgpt.confidence}%)
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={async () => {
                            const result = await AIConsensusAPI.executeOpportunity(opp.id);
                            if (result.success) {
                              await refreshData();
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '12px',
                            background: opp.action === 'buy' ? '#22C55E' : '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Icons.Check size={18} />
                          Execute {opp.action.toUpperCase()}
                        </button>
                        <button
                          onClick={async () => {
                            await AIConsensusAPI.rejectOpportunity(opp.id);
                            await refreshData();
                          }}
                          style={{
                            padding: '12px 20px',
                            background: 'var(--border)',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <Icons.X size={18} />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trading Pairs Configuration */}
            <div>
              <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.Settings size={18} />
                Monitored Pairs
              </h3>

              <div className="card" style={{ padding: '16px' }}>
                {tradingPairs.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '20px' }}>
                    <p>No trading pairs configured</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {tradingPairs.map((pair) => (
                      <div
                        key={pair.symbol}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          background: pair.enabled ? 'var(--bg)' : 'var(--border)',
                          borderRadius: '8px',
                          opacity: pair.enabled ? 1 : 0.6
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: pair.enabled ? '#22C55E' : '#6B7280'
                          }} />
                          <span style={{ fontWeight: 600 }}>{pair.symbol}</span>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            background: pair.tradingMode === 'futures' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                            color: pair.tradingMode === 'futures' ? '#8B5CF6' : '#22C55E'
                          }}>
                            {pair.tradingMode}
                          </span>
                          {pair.leverage && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 600,
                              background: 'rgba(245, 158, 11, 0.2)',
                              color: '#F59E0B'
                            }}>
                              {pair.leverage}x
                            </span>
                          )}
                        </div>
                        <button
                          onClick={async () => {
                            await AIConsensusAPI.removeTradingPair(pair.symbol);
                            const pairs = await AIConsensusAPI.getTradingPairs();
                            setTradingPairs(pairs);
                          }}
                          style={{
                            padding: '4px 8px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: 'var(--muted)'
                          }}
                        >
                          <Icons.X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Pair */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Add Trading Pair</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="BTCUSDT"
                      id="new-pair-symbol"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--bg)',
                        fontSize: '14px'
                      }}
                    />
                    <select
                      id="new-pair-mode"
                      style={{
                        padding: '8px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--bg)',
                        fontSize: '14px'
                      }}
                    >
                      <option value="spot">Spot</option>
                      <option value="futures">Futures</option>
                    </select>
                    <button
                      onClick={async () => {
                        const symbolInput = document.getElementById('new-pair-symbol') as HTMLInputElement;
                        const modeSelect = document.getElementById('new-pair-mode') as HTMLSelectElement;
                        if (symbolInput.value) {
                          await AIConsensusAPI.addTradingPair({
                            symbol: symbolInput.value.toUpperCase(),
                            exchange: 'binance',
                            tradingMode: modeSelect.value as 'spot' | 'futures',
                            enabled: true
                          });
                          const pairs = await AIConsensusAPI.getTradingPairs();
                          setTradingPairs(pairs);
                          symbolInput.value = '';
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        background: 'var(--accent)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      <Icons.Plus size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Auto-execute Settings */}
              <div className="card" style={{ padding: '16px', marginTop: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icons.Zap size={14} />
                  Auto-Execute Settings
                </div>
                <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                  <div style={{ marginBottom: '8px' }}>
                    Auto-execute: <strong>{autoAnalysisStatus?.config?.autoExecute ? 'Enabled' : 'Disabled'}</strong>
                  </div>
                  {autoAnalysisStatus?.config?.autoExecute && (
                    <div>
                      Min confidence: <strong>{autoAnalysisStatus.config.autoExecuteMinConfidence}%</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* AI Supervised Bots Section */}
          <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #F97316 50%, #22C55E 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icons.Bot size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px' }}>AI-Supervised Trading Bots</h3>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                    Automated strategies with AI consensus supervision
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {botServiceStatus && (
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: botServiceStatus.totalBots > 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                    color: botServiceStatus.totalBots > 0 ? '#22C55E' : '#6B7280'
                  }}>
                    {botServiceStatus.runningBots} / {botServiceStatus.totalBots} Running
                  </span>
                )}
                <button
                  onClick={() => setShowCreateBotModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--accent)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icons.Plus size={18} />
                  Create Bot
                </button>
              </div>
            </div>

            {/* Bots Grid */}
            {bots.length === 0 ? (
              <div className="card" style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)' }}>
                <Icons.Bot size={56} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <p style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>No Trading Bots Yet</p>
                <p style={{ fontSize: '14px' }}>
                  Create your first AI-supervised bot to start automated trading with intelligent oversight.
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '16px' }}>
                {bots.map((bot) => (
                  <div
                    key={bot.id}
                    className="card"
                    style={{
                      padding: '20px',
                      border: `2px solid ${bot.status === 'running' ? '#22C55E' : bot.status === 'paused' ? '#F59E0B' : 'var(--border)'}`
                    }}
                  >
                    {/* Bot Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 700, fontSize: '16px' }}>{bot.name}</span>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            background: bot.status === 'running' ? 'rgba(34, 197, 94, 0.2)' : bot.status === 'paused' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                            color: bot.status === 'running' ? '#22C55E' : bot.status === 'paused' ? '#F59E0B' : '#6B7280'
                          }}>
                            {bot.status}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', gap: '8px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: 'rgba(139, 92, 246, 0.15)',
                            color: '#8B5CF6'
                          }}>
                            {bot.type.replace('_', ' ')}
                          </span>
                          {bot.aiSupervisionEnabled && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: 'rgba(34, 197, 94, 0.15)',
                              color: '#22C55E'
                            }}>
                              AI Supervised
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {bot.status === 'stopped' && (
                          <button
                            onClick={async () => {
                              await AIConsensusAPI.startBot(bot.id);
                              await refreshData();
                            }}
                            style={{
                              padding: '6px',
                              background: '#22C55E',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              color: 'white'
                            }}
                            title="Start Bot"
                          >
                            <Icons.Play size={14} />
                          </button>
                        )}
                        {bot.status === 'running' && (
                          <>
                            <button
                              onClick={async () => {
                                await AIConsensusAPI.pauseBot(bot.id);
                                await refreshData();
                              }}
                              style={{
                                padding: '6px',
                                background: '#F59E0B',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: 'white'
                              }}
                              title="Pause Bot"
                            >
                              <Icons.Pause size={14} />
                            </button>
                            <button
                              onClick={async () => {
                                await AIConsensusAPI.stopBot(bot.id);
                                await refreshData();
                              }}
                              style={{
                                padding: '6px',
                                background: '#EF4444',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: 'white'
                              }}
                              title="Stop Bot"
                            >
                              <Icons.Square size={14} />
                            </button>
                          </>
                        )}
                        {bot.status === 'paused' && (
                          <>
                            <button
                              onClick={async () => {
                                await AIConsensusAPI.resumeBot(bot.id);
                                await refreshData();
                              }}
                              style={{
                                padding: '6px',
                                background: '#22C55E',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: 'white'
                              }}
                              title="Resume Bot"
                            >
                              <Icons.Play size={14} />
                            </button>
                            <button
                              onClick={async () => {
                                await AIConsensusAPI.stopBot(bot.id);
                                await refreshData();
                              }}
                              style={{
                                padding: '6px',
                                background: '#EF4444',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: 'white'
                              }}
                              title="Stop Bot"
                            >
                              <Icons.Square size={14} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={async () => {
                            if (confirm(`Delete bot "${bot.name}"?`)) {
                              await AIConsensusAPI.deleteBot(bot.id);
                              await refreshData();
                            }
                          }}
                          style={{
                            padding: '6px',
                            background: 'var(--border)',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: 'var(--muted)'
                          }}
                          title="Delete Bot"
                        >
                          <Icons.Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Bot Stats */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '12px',
                      padding: '12px',
                      background: 'var(--bg)',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Trades</div>
                        <div style={{ fontWeight: 700, fontSize: '14px' }}>{bot.stats.totalTrades}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Win Rate</div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: bot.stats.totalTrades > 0 ? (bot.stats.winningTrades / bot.stats.totalTrades * 100 >= 50 ? '#22C55E' : '#EF4444') : 'inherit' }}>
                          {bot.stats.totalTrades > 0 ? (bot.stats.winningTrades / bot.stats.totalTrades * 100).toFixed(0) : 0}%
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>P&L</div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: bot.stats.totalPnl >= 0 ? '#22C55E' : '#EF4444' }}>
                          ${bot.stats.totalPnl.toFixed(2)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Uptime</div>
                        <div style={{ fontWeight: 700, fontSize: '14px' }}>
                          {bot.stats.uptimeMinutes > 60 ? `${Math.floor(bot.stats.uptimeMinutes / 60)}h` : `${bot.stats.uptimeMinutes}m`}
                        </div>
                      </div>
                    </div>

                    {/* AI Recommendations */}
                    {bot.aiSupervisionEnabled && bot.pendingRecommendations.length > 0 && (
                      <div style={{
                        padding: '12px',
                        background: 'rgba(139, 92, 246, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(139, 92, 246, 0.3)'
                      }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#8B5CF6', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icons.Brain size={14} />
                          AI Recommendations ({bot.pendingRecommendations.length})
                        </div>
                        {bot.pendingRecommendations.slice(0, 2).map((rec) => (
                          <div key={rec.id} style={{
                            padding: '8px',
                            background: 'var(--bg)',
                            borderRadius: '6px',
                            marginBottom: '6px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                              <span style={{ fontWeight: 600, fontSize: '12px' }}>{rec.title}</span>
                              <span style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: rec.priority === 'critical' ? 'rgba(239, 68, 68, 0.2)' : rec.priority === 'high' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                                color: rec.priority === 'critical' ? '#EF4444' : rec.priority === 'high' ? '#F59E0B' : '#22C55E'
                              }}>
                                {rec.priority}
                              </span>
                            </div>
                            <p style={{ fontSize: '11px', color: 'var(--muted)', margin: '0 0 8px' }}>{rec.description}</p>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
                                Consensus: {rec.consensusScore.toFixed(0)}%
                              </div>
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                                <button
                                  onClick={async () => {
                                    await AIConsensusAPI.applyBotRecommendation(bot.id, rec.id);
                                    await refreshData();
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    background: '#22C55E',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    color: 'white',
                                    fontSize: '10px',
                                    fontWeight: 600
                                  }}
                                >
                                  Apply
                                </button>
                                <button
                                  onClick={async () => {
                                    await AIConsensusAPI.dismissBotRecommendation(bot.id, rec.id);
                                    await refreshData();
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    background: 'var(--border)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    fontWeight: 600
                                  }}
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {bot.pendingRecommendations.length > 2 && (
                          <div style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'center', marginTop: '8px' }}>
                            +{bot.pendingRecommendations.length - 2} more recommendations
                          </div>
                        )}
                      </div>
                    )}

                    {/* Trigger AI Review Button */}
                    {bot.aiSupervisionEnabled && bot.status === 'running' && (
                      <button
                        onClick={async () => {
                          await AIConsensusAPI.triggerBotAIReview(bot.id);
                          await refreshData();
                        }}
                        style={{
                          width: '100%',
                          marginTop: '12px',
                          padding: '8px',
                          background: 'var(--border)',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <Icons.Brain size={14} />
                        Request AI Review
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create Bot Modal */}
          {showCreateBotModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'var(--card)',
                borderRadius: '16px',
                padding: '24px',
                width: '500px',
                maxHeight: '80vh',
                overflow: 'auto'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px' }}>Create AI-Supervised Bot</h3>
                  <button
                    onClick={() => {
                      setShowCreateBotModal(false);
                      setSelectedBotType(null);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    <Icons.X size={20} />
                  </button>
                </div>

                {/* Bot Type Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>Select Strategy Type</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {[
                      { type: 'infinity_grid' as BotType, name: 'Infinity Grid', desc: 'Grid trading with dynamic range adjustment', color: '#8B5CF6' },
                      { type: 'smart_dca' as BotType, name: 'Smart DCA', desc: 'Dollar-cost averaging with volatility timing', color: '#F97316' },
                      { type: 'ma200_dca' as BotType, name: 'MA200 DCA', desc: 'DCA based on 200-day moving average', color: '#22C55E' },
                      { type: 'btc_rebalancer' as BotType, name: 'BTC Rebalancer', desc: 'Portfolio rebalancing around BTC target', color: '#EF4444' },
                    ].map((strategy) => (
                      <button
                        key={strategy.type}
                        onClick={() => setSelectedBotType(strategy.type)}
                        style={{
                          padding: '16px',
                          background: selectedBotType === strategy.type ? `${strategy.color}20` : 'var(--bg)',
                          border: `2px solid ${selectedBotType === strategy.type ? strategy.color : 'var(--border)'}`,
                          borderRadius: '10px',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: '4px', color: strategy.color }}>{strategy.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{strategy.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bot Configuration Form */}
                {selectedBotType && (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target as HTMLFormElement);
                      const name = formData.get('name') as string;
                      const symbol = formData.get('symbol') as string;
                      const aiSupervision = formData.get('aiSupervision') === 'on';

                      if (!name || !symbol) return;

                      const defaultConfig: Record<BotType, Record<string, unknown>> = {
                        infinity_grid: {
                          symbol,
                          gridLevels: 10,
                          gridSpacing: 0.5,
                          orderSize: 0.001,
                          rangeAdjustment: 'auto',
                        },
                        smart_dca: {
                          symbol,
                          baseOrderSize: 50,
                          safetyOrderSize: 100,
                          maxSafetyOrders: 5,
                          priceDeviationToOpen: 2.5,
                          safetyOrderPriceScale: 1.5,
                        },
                        ma200_dca: {
                          symbol,
                          maLength: 200,
                          buyThreshold: 5,
                          orderSize: 100,
                          maxPositions: 10,
                        },
                        btc_rebalancer: {
                          targetAllocation: 50,
                          rebalanceThreshold: 5,
                          rebalanceInterval: 'daily',
                        },
                      };

                      await AIConsensusAPI.createBot({
                        name,
                        type: selectedBotType,
                        config: defaultConfig[selectedBotType],
                        aiSupervisionEnabled: aiSupervision,
                      });

                      setShowCreateBotModal(false);
                      setSelectedBotType(null);
                      await refreshData();
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Bot Name</label>
                      <input
                        name="name"
                        required
                        placeholder={`My ${selectedBotType.replace('_', ' ')} Bot`}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          background: 'var(--bg)',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    {selectedBotType !== 'btc_rebalancer' && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Trading Symbol</label>
                        <input
                          name="symbol"
                          required
                          defaultValue="BTCUSDT"
                          placeholder="BTCUSDT"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            background: 'var(--bg)',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name="aiSupervision"
                          defaultChecked={true}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <div>
                          <span style={{ fontWeight: 600, fontSize: '13px' }}>Enable AI Supervision</span>
                          <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--muted)' }}>
                            Claude, Grok & ChatGPT will monitor and suggest optimizations
                          </p>
                        </div>
                      </label>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateBotModal(false);
                          setSelectedBotType(null);
                        }}
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: 'var(--border)',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: 'var(--accent)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        Create Bot
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Decisions Tab */}
      {activeTab === 'decisions' && (
        <div>
          {/* Notification Banners */}
          {executeError && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              color: '#EF4444',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Icons.AlertTriangle size={20} />
              <span style={{ fontWeight: 500 }}>Error: {executeError}</span>
              <button
                onClick={() => setExecuteError(null)}
                style={{
                  marginLeft: 'auto',
                  background: 'transparent',
                  border: 'none',
                  color: '#EF4444',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <Icons.X size={16} />
              </button>
            </div>
          )}

          {executeSuccess && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid #22C55E',
              borderRadius: '8px',
              color: '#22C55E',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Icons.CheckCircle size={20} />
              <span style={{ fontWeight: 500 }}>{executeSuccess}</span>
              <button
                onClick={() => setExecuteSuccess(null)}
                style={{
                  marginLeft: 'auto',
                  background: 'transparent',
                  border: 'none',
                  color: '#22C55E',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <Icons.X size={16} />
              </button>
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600 }}>Trading Decisions</h2>
            <button
              onClick={refreshData}
              style={{
                padding: '8px 16px',
                background: 'var(--border)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px'
              }}
            >
              <Icons.RefreshCw size={14} />
              Refresh
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {decisions.map((decision) => (
              <div key={decision.id} className="card" style={{ padding: '20px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '18px', fontWeight: 700 }}>{decision.symbol}</span>
                      <span style={{
                        padding: '4px 10px',
                        background: getActionBg(decision.consensusAction),
                        color: getActionColor(decision.consensusAction),
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase'
                      }}>
                        {decision.consensusAction}
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        background: 'var(--border)',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}>
                        {decision.consensusConfidence}% confidence
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                      {new Date(decision.createdAt).toLocaleString()}
                    </div>
                  </div>

                  {!decision.executed && decision.consensusAction !== 'human_review' && (
                    <button
                      onClick={() => openExecuteModal(decision)}
                      disabled={executingId === decision.id}
                      style={{
                        padding: '10px 20px',
                        background: executingId === decision.id
                          ? 'var(--muted)'
                          : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: executingId === decision.id ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {executingId === decision.id ? (
                        <>
                          <Icons.RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Executing...
                        </>
                      ) : (
                        <>
                          <Icons.Zap size={16} />
                          Execute
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px'
                }}>
                  {/* Claude */}
                  <div style={{
                    padding: '12px',
                    background: 'var(--border)',
                    borderRadius: '10px',
                    borderLeft: `3px solid ${AI_COLORS.claude}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px' }}>Claude</span>
                      {decision.claudeResponse && (
                        <span style={{
                          padding: '2px 6px',
                          background: getActionBg(decision.claudeResponse.action),
                          color: getActionColor(decision.claudeResponse.action),
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 700,
                          textTransform: 'uppercase'
                        }}>
                          {decision.claudeResponse.action}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0, lineHeight: 1.4 }}>
                      {decision.claudeResponse?.reasoning || 'No response'}
                    </p>
                  </div>

                  {/* Grok */}
                  <div style={{
                    padding: '12px',
                    background: 'var(--border)',
                    borderRadius: '10px',
                    borderLeft: `3px solid ${AI_COLORS.grok}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px' }}>Grok</span>
                      {decision.grokResponse && (
                        <span style={{
                          padding: '2px 6px',
                          background: getActionBg(decision.grokResponse.action),
                          color: getActionColor(decision.grokResponse.action),
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 700,
                          textTransform: 'uppercase'
                        }}>
                          {decision.grokResponse.action}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0, lineHeight: 1.4 }}>
                      {decision.grokResponse?.reasoning || 'No response'}
                    </p>
                  </div>

                  {/* ChatGPT */}
                  <div style={{
                    padding: '12px',
                    background: 'var(--border)',
                    borderRadius: '10px',
                    borderLeft: `3px solid ${AI_COLORS.chatgpt}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px' }}>ChatGPT</span>
                      {decision.chatgptResponse && (
                        <span style={{
                          padding: '2px 6px',
                          background: getActionBg(decision.chatgptResponse.action),
                          color: getActionColor(decision.chatgptResponse.action),
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 700,
                          textTransform: 'uppercase'
                        }}>
                          {decision.chatgptResponse.action}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0, lineHeight: 1.4 }}>
                      {decision.chatgptResponse?.reasoning || 'No response'}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {decisions.length === 0 && (
              <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
                <Icons.Inbox size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <p style={{ color: 'var(--muted)', fontSize: '16px' }}>
                  No decisions yet. Use "Analyze Now" to get AI consensus.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Other tabs - placeholder */}
      {activeTab === 'prompts' && (
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <Icons.Edit size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
          <h3 style={{ marginBottom: '8px' }}>Prompt Builder</h3>
          <p style={{ color: 'var(--muted)' }}>Configure AI trading prompts (coming soon)</p>
        </div>
      )}

      {activeTab === 'exchanges' && (
        <ExchangesTab
          exchanges={exchanges}
          isAuthenticated={isAuthenticated}
          onShowAuthModal={() => setShowAuthModal(true)}
          onRefresh={async () => {
            const data = await AIConsensusAPI.getExchanges();
            setExchanges(data);
          }}
        />
      )}

      {/* API Status Tab */}
      {activeTab === 'status' && (
        <div>
          {/* Overall Status Banner */}
          <div className="card" style={{
            padding: '20px',
            marginBottom: '20px',
            background: apiStatus?.overall === 'healthy' ? 'rgba(34, 197, 94, 0.1)' :
                       apiStatus?.overall === 'degraded' ? 'rgba(245, 158, 11, 0.1)' :
                       'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${apiStatus?.overall === 'healthy' ? '#22C55E' :
                                apiStatus?.overall === 'degraded' ? '#F59E0B' : '#EF4444'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: apiStatus?.overall === 'healthy' ? '#22C55E' :
                             apiStatus?.overall === 'degraded' ? '#F59E0B' : '#EF4444',
                  animation: 'pulse 2s infinite'
                }} />
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px' }}>
                    System Status: {apiStatus?.overall?.toUpperCase() || 'UNKNOWN'}
                  </h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '14px' }}>
                    {apiStatus?.summary?.operational || 0} operational • {apiStatus?.summary?.degraded || 0} degraded • {apiStatus?.summary?.down || 0} down • {apiStatus?.summary?.unconfigured || 0} unconfigured
                  </p>
                </div>
              </div>
              <button
                onClick={refreshData}
                style={{
                  padding: '8px 16px',
                  background: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Icons.RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {/* API Categories */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            {/* AI APIs */}
            <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.Cpu size={20} style={{ color: '#8B5CF6' }} />
                AI Providers
              </h3>
              {apiStatus?.apis?.ai && Object.entries(apiStatus.apis.ai).map(([name, metrics]) => (
                <APIStatusCard key={name} name={name} metrics={metrics} />
              ))}
              {!apiStatus?.apis?.ai && <p style={{ color: 'var(--muted)' }}>Loading...</p>}
            </div>

            {/* Market APIs */}
            <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.TrendingUp size={20} style={{ color: '#22C55E' }} />
                Market Data
              </h3>
              {apiStatus?.apis?.market && Object.entries(apiStatus.apis.market).map(([name, metrics]) => (
                <APIStatusCard key={name} name={name} metrics={metrics} />
              ))}
              {!apiStatus?.apis?.market && <p style={{ color: 'var(--muted)' }}>Loading...</p>}
            </div>

            {/* Exchange APIs */}
            <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.Wallet size={20} style={{ color: '#F97316' }} />
                Exchanges
              </h3>
              {apiStatus?.apis?.exchange && Object.entries(apiStatus.apis.exchange).map(([name, metrics]) => (
                <APIStatusCard key={name} name={name} metrics={metrics} />
              ))}
              {!apiStatus?.apis?.exchange && <p style={{ color: 'var(--muted)' }}>Loading...</p>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Stats Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent)' }}>
                {logStats?.total?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Total Logs</div>
            </div>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#22C55E' }}>
                {logStats?.last24Hours?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Last 24h</div>
            </div>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#EF4444' }}>
                {logStats?.errorCount?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Errors (24h)</div>
            </div>
            <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#F59E0B' }}>
                {logStats?.warnCount?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Warnings (24h)</div>
            </div>
          </div>

          {/* Filters */}
          <div className="card" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Level Filter */}
              <select
                aria-label="Filter logs by level"
                value={logFilter.level || ''}
                onChange={(e) => setLogFilter(prev => ({ ...prev, level: e.target.value || undefined }))}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--card-bg)',
                  color: 'var(--foreground)',
                  fontSize: '14px',
                  minWidth: '120px',
                }}
              >
                <option value="">All Levels</option>
                {logLevels.map(l => (
                  <option key={l.level} value={l.level}>
                    {l.level.toUpperCase()} ({l.count})
                  </option>
                ))}
              </select>

              {/* Category Filter */}
              <select
                aria-label="Filter logs by category"
                value={logFilter.category || ''}
                onChange={(e) => setLogFilter(prev => ({ ...prev, category: e.target.value || undefined }))}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--card-bg)',
                  color: 'var(--foreground)',
                  fontSize: '14px',
                  minWidth: '150px',
                }}
              >
                <option value="">All Categories</option>
                {logCategories.filter(c => c.category).map(c => (
                  <option key={c.category} value={c.category || ''}>
                    {c.category} ({c.count})
                  </option>
                ))}
              </select>

              {/* Search */}
              <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <Icons.Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={logFilter.search || ''}
                  onChange={(e) => setLogFilter(prev => ({ ...prev, search: e.target.value || undefined }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 36px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    background: 'var(--card-bg)',
                    color: 'var(--foreground)',
                    fontSize: '14px',
                  }}
                />
              </div>

              {/* Refresh Button */}
              <button
                onClick={() => fetchLogs(false)}
                disabled={logsLoading}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--card-bg)',
                  color: 'var(--foreground)',
                  cursor: logsLoading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Icons.RefreshCw size={14} style={{ animation: logsLoading ? 'spin 1s linear infinite' : 'none' }} />
                Refresh
              </button>

              {/* Clear Filters */}
              {(logFilter.level || logFilter.category || logFilter.search) && (
                <button
                  onClick={() => setLogFilter({})}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    background: 'transparent',
                    color: 'var(--muted)',
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Logs List */}
          <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {logsLoading && logs.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center' }}>
                  <Icons.RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', opacity: 0.5, marginBottom: '12px' }} />
                  <p style={{ color: 'var(--muted)' }}>Loading logs...</p>
                </div>
              ) : logs.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center' }}>
                  <Icons.FileText size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                  <h3 style={{ marginBottom: '8px' }}>No Logs Found</h3>
                  <p style={{ color: 'var(--muted)' }}>
                    {logFilter.level || logFilter.category || logFilter.search
                      ? 'Try adjusting your filters'
                      : 'Logs will appear here as the system runs'}
                  </p>
                </div>
              ) : (
                /* V2.I.7.f — table migrée vers ResponsiveTable. */
                <ResponsiveTable<SystemLog>
                  data={logs}
                  keyExtractor={(l) => l.id}
                  columns={[
                    {
                      key: 'time',
                      header: 'Time',
                      isSubtitle: true,
                      render: (l) => (
                        <span style={{ fontFamily: 'monospace', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                          {new Date(l.createdAt).toLocaleString()}
                        </span>
                      ),
                    },
                    {
                      key: 'level',
                      header: 'Level',
                      width: '80px',
                      render: (l) => (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          background: l.level === 'error' ? 'rgba(239, 68, 68, 0.15)' :
                                      l.level === 'warn' ? 'rgba(245, 158, 11, 0.15)' :
                                      l.level === 'info' ? 'rgba(59, 130, 246, 0.15)' :
                                      'rgba(107, 114, 128, 0.15)',
                          color: l.level === 'error' ? '#EF4444' :
                                 l.level === 'warn' ? '#F59E0B' :
                                 l.level === 'info' ? '#3B82F6' :
                                 '#6B7280',
                        }}>
                          {l.level}
                        </span>
                      ),
                    },
                    {
                      key: 'category',
                      header: 'Category',
                      width: '120px',
                      render: (l) => (
                        <span style={{ color: 'var(--muted)' }}>{l.category || '—'}</span>
                      ),
                    },
                    {
                      key: 'message',
                      header: 'Message',
                      isTitle: true,
                      render: (l) => (
                        <>
                          <div style={{ maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.message}
                          </div>
                          {l.metadata && Object.keys(l.metadata).length > 0 && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace' }}>
                              {JSON.stringify(l.metadata).substring(0, 100)}
                              {JSON.stringify(l.metadata).length > 100 && '…'}
                            </div>
                          )}
                        </>
                      ),
                    },
                  ]}
                />
              )}
            </div>

            {/* Pagination / Load More */}
            {logsPagination.hasMore && (
              <div style={{ padding: '16px', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
                <button
                  onClick={() => fetchLogs(true)}
                  disabled={logsLoading}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    background: 'var(--card-bg)',
                    color: 'var(--foreground)',
                    cursor: logsLoading ? 'wait' : 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {logsLoading ? 'Loading...' : `Load More (${logsPagination.total - logs.length} remaining)`}
                </button>
              </div>
            )}

            {/* Total Count */}
            {logs.length > 0 && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', fontSize: '13px', color: 'var(--muted)', textAlign: 'center' }}>
                Showing {logs.length} of {logsPagination.total.toLocaleString()} logs
              </div>
            )}
          </div>
        </div>
      )}

      {/* Execution Modal */}
      {showExecuteModal && selectedDecision && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeExecuteModal}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: '500px',
              margin: '16px',
              padding: '24px',
              animation: 'fadeInScale 0.2s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icons.Zap size={24} style={{ color: 'var(--accent)' }} />
                Execute Trade
              </h2>
              <button
                onClick={closeExecuteModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--muted)',
                }}
              >
                <Icons.X size={24} />
              </button>
            </div>

            {/* Decision Info */}
            <div style={{
              padding: '16px',
              background: getActionBg(actionOverride || selectedDecision.consensusAction),
              borderRadius: '12px',
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '4px' }}>Symbol</div>
                  <div style={{ fontSize: '20px', fontWeight: 700 }}>{selectedDecision.symbol}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '4px' }}>AI Consensus</div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: getActionColor(selectedDecision.consensusAction),
                    textTransform: 'uppercase',
                  }}>
                    {selectedDecision.consensusAction}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '16px' }}>
                <div>
                  <span style={{ color: 'var(--muted)', fontSize: '13px' }}>Confidence: </span>
                  <span style={{ fontWeight: 600 }}>{selectedDecision.consensusConfidence}%</span>
                </div>
                <div>
                  <span style={{ color: 'var(--muted)', fontSize: '13px' }}>Votes: </span>
                  <span style={{ fontWeight: 600 }}>
                    {[selectedDecision.claudeResponse, selectedDecision.grokResponse, selectedDecision.chatgptResponse]
                      .filter(r => r?.action === selectedDecision.consensusAction).length}/3
                  </span>
                </div>
              </div>
            </div>

            {/* Position Size */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontWeight: 600 }}>Position Size</label>
                <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{positionPercent}%</span>
              </div>
              <input
                type="range"
                min="1"
                max="25"
                value={positionPercent}
                onChange={(e) => setPositionPercent(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  accentColor: 'var(--accent)',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                <span>Conservative (1%)</span>
                <span>Aggressive (25%)</span>
              </div>

              {/* Estimated Value */}
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'var(--border)',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
                <span style={{ color: 'var(--muted)' }}>Estimated Investment</span>
                <span style={{ fontWeight: 700 }}>
                  ${((tradingAccount?.account?.totalValueUsdt || 10000) * positionPercent / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Action Override */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>Action Override (Optional)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(['buy', 'sell', 'hold'] as const).map((action) => (
                  <button
                    key={action}
                    onClick={() => setActionOverride(actionOverride === action ? null : action)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: actionOverride === action ? `2px solid ${getActionColor(action)}` : '1px solid var(--border)',
                      borderRadius: '8px',
                      background: actionOverride === action ? getActionBg(action) : 'var(--bg)',
                      color: actionOverride === action ? getActionColor(action) : 'var(--text)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {action}
                  </button>
                ))}
              </div>
              {actionOverride && (
                <p style={{ fontSize: '12px', color: '#F59E0B', marginTop: '8px' }}>
                  ⚠️ You are overriding the AI consensus ({selectedDecision.consensusAction} → {actionOverride})
                </p>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={closeExecuteModal}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'var(--border)',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmExecution}
                style={{
                  flex: 2,
                  padding: '14px',
                  background: `linear-gradient(135deg, ${getActionColor(actionOverride || selectedDecision.consensusAction)}, ${getActionColor(actionOverride || selectedDecision.consensusAction)}dd)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <Icons.Zap size={18} />
                Execute {actionOverride?.toUpperCase() || selectedDecision.consensusAction.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Auth Modal */}
      <AIConsensusAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          window.location.reload(); // Reload to fetch user-specific data
        }}
      />
    </div>
  );
}

// Wrapper with AuthProvider
export function AIConsensusBot() {
  return (
    <AuthProvider>
      <AIConsensusBotContent />
    </AuthProvider>
  );
}

export default AIConsensusBot;
